import { Component, HostListener, inject, signal, computed, OnInit, OnDestroy, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { timer, switchMap, filter, take, takeWhile, tap } from 'rxjs';
import { AgentService, AgentResponse, AgentRunResponse, CreateAgentRequest, UpdateAgentRequest } from '../../core/services/agent';
import { ApplicationService, RegisteredAppResponse } from '../../core/services/application';
import { ConceptService, ConceptResponse } from '../../core/services/concept';
import { AuthService } from '../../core/services/auth';

const PAGE_SIZE = 20;

@Component({
  selector: 'app-agents',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, DecimalPipe],
  templateUrl: './agents.html',
  styleUrl: './agents.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentsComponent implements OnInit, OnDestroy {
  private agentService = inject(AgentService);
  private appService = inject(ApplicationService);
  private conceptService = inject(ConceptService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  canManageRuns = computed(() => {
    const roles = this.authService.currentUser()?.roles as string[] | undefined;
    return roles?.includes('super_admin') || roles?.includes('admin') || false;
  });

  agents = signal<AgentResponse[]>([]);
  apps = signal<RegisteredAppResponse[]>([]);
  conceptsByApp = signal<ConceptResponse[]>([]);
  isLoading = signal(false);
  appsLoading = signal(false);
  appsError = signal<string | null>(null);
  conceptsLoading = signal(false);
  errorMsg = signal<string | null>(null);

  filterAppId = signal('');
  filterIsActive = signal<'all' | 'true' | 'false'>('all');
  appFilterOpen = signal(false);
  appFilterClosing = signal(false);
  statusFilterOpen = signal(false);
  statusFilterClosing = signal(false);
  createClosing = signal(false);
  editClosing = signal(false);
  deleteClosing = signal(false);
  private appFilterTimer: ReturnType<typeof setTimeout> | null = null;
  private statusFilterTimer: ReturnType<typeof setTimeout> | null = null;
  private createCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private editCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private deleteCloseTimer: ReturnType<typeof setTimeout> | null = null;

  page = signal(0);
  readonly pageSize = PAGE_SIZE;
  hasPrev = computed(() => this.page() > 0);
  hasNext = computed(() => this.agents().length === PAGE_SIZE);

  showCreateModal = signal(false);
  showEditModal = signal(false);
  showDeleteModal = signal(false);
  selectedAgent = signal<AgentResponse | null>(null);
  isSaving = signal(false);
  saveError = signal<string | null>(null);

  runProgress = signal<Map<string, AgentRunResponse | null>>(new Map());
  private activePolls = new Set<string>();

  agentForm = this.fb.group({
    app_id: ['', Validators.required],
    concept_ids: [[] as string[], [Validators.required, (c: AbstractControl) => Array.isArray(c.value) && c.value.length > 0 ? null : { required: true }]],
    content_runs_per_concept: [1, [Validators.required, Validators.min(1)]],
    trigger_interval: ['', Validators.required],
    is_active: [true],
  });

  ngOnInit(): void {
    this.loadApps();
    this.loadAgents();
    if (this.canManageRuns()) {
      this.resumeActiveRuns();
    }
  }

  ngOnDestroy(): void {
    for (const timer of [this.appFilterTimer, this.statusFilterTimer, this.createCloseTimer, this.editCloseTimer, this.deleteCloseTimer]) {
      if (timer) clearTimeout(timer);
    }
  }

  @HostListener('document:click')
  closeFiltersFromOutside(): void {
    this.closeAppFilter();
    this.closeStatusFilter();
  }

  @HostListener('document:keydown.escape')
  closeFiltersFromEscape(): void {
    this.closeAppFilter();
    this.closeStatusFilter();
  }

  loadApps(): void {
    this.appsLoading.set(true);
    this.appsError.set(null);
    this.appService.getApps(0, 100).subscribe({
      next: list => { this.apps.set(list); this.appsLoading.set(false); },
      error: () => { this.appsLoading.set(false); this.appsError.set('Failed to load apps.'); },
    });
  }

  loadConceptsForApp(app_id: string): void {
    if (!app_id) { this.conceptsByApp.set([]); return; }
    this.conceptsLoading.set(true);
    this.conceptService.list({ app_id, limit: 100 }).subscribe({
      next: list => { this.conceptsByApp.set(list); this.conceptsLoading.set(false); },
      error: () => this.conceptsLoading.set(false),
    });
  }

  loadAgents(): void {
    this.isLoading.set(true);
    this.errorMsg.set(null);
    const isActive = this.filterIsActive() === 'all' ? null : this.filterIsActive() === 'true';
    this.agentService.list({
      app_id: this.filterAppId() || null,
      is_active: isActive,
      skip: this.page() * PAGE_SIZE,
      limit: PAGE_SIZE,
    }).subscribe({
      next: list => { this.agents.set(list); this.isLoading.set(false); },
      error: err => { this.errorMsg.set(err?.error?.detail ?? 'Failed to load agents.'); this.isLoading.set(false); },
    });
  }

  applyFilters(): void {
    this.page.set(0);
    this.loadAgents();
  }

  appFilterLabel(): string {
    return this.filterAppId() ? this.getAppName(this.filterAppId()) : 'All Apps';
  }

  statusFilterLabel(): string {
    if (this.filterIsActive() === 'true') return 'Active';
    if (this.filterIsActive() === 'false') return 'Inactive';
    return 'All';
  }

  toggleAppFilter(): void {
    if (this.appFilterOpen()) {
      this.closeAppFilter();
      return;
    }
    this.closeStatusFilter();
    this.clearTimer(this.appFilterTimer);
    this.appFilterTimer = null;
    this.appFilterClosing.set(false);
    this.appFilterOpen.set(true);
  }

  toggleStatusFilter(): void {
    if (this.statusFilterOpen()) {
      this.closeStatusFilter();
      return;
    }
    this.closeAppFilter();
    this.clearTimer(this.statusFilterTimer);
    this.statusFilterTimer = null;
    this.statusFilterClosing.set(false);
    this.statusFilterOpen.set(true);
  }

  chooseAppFilter(appId: string): void {
    if (appId !== this.filterAppId()) {
      this.filterAppId.set(appId);
      this.applyFilters();
    }
    this.closeAppFilter();
  }

  chooseStatusFilter(status: 'all' | 'true' | 'false'): void {
    if (status !== this.filterIsActive()) {
      this.filterIsActive.set(status);
      this.applyFilters();
    }
    this.closeStatusFilter();
  }

  closeAppFilter(): void {
    this.appFilterTimer = this.beginClose(this.appFilterOpen, this.appFilterClosing, this.appFilterTimer, () => {
      this.appFilterOpen.set(false);
      this.appFilterClosing.set(false);
      this.appFilterTimer = null;
    });
  }

  closeStatusFilter(): void {
    this.statusFilterTimer = this.beginClose(this.statusFilterOpen, this.statusFilterClosing, this.statusFilterTimer, () => {
      this.statusFilterOpen.set(false);
      this.statusFilterClosing.set(false);
      this.statusFilterTimer = null;
    });
  }

  onAppIdChange(app_id: string): void {
    this.loadConceptsForApp(app_id);
    this.agentForm.patchValue({ concept_ids: [] });
  }

  toggleConceptId(concept_id: string, checked: boolean): void {
    const current = this.agentForm.get('concept_ids')?.value ?? [];
    const next = checked ? [...current, concept_id] : current.filter((id: string) => id !== concept_id);
    this.agentForm.patchValue({ concept_ids: next });
  }

  prevPage(): void {
    if (this.hasPrev()) { this.page.update(p => p - 1); this.loadAgents(); }
  }

  nextPage(): void {
    if (this.hasNext()) { this.page.update(p => p + 1); this.loadAgents(); }
  }

  getAppName(app_id: string): string {
    return this.apps().find(a => a.app_id === app_id)?.app_name ?? app_id;
  }

  getConceptTitles(agent: AgentResponse): string {
    return agent.concept_ids.length ? `${agent.concept_ids.length} concept(s)` : '—';
  }

  formatNextTrigger(nextTriggerAt: string | null): string {
    if (!nextTriggerAt) return '—';
    const utc = nextTriggerAt.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(nextTriggerAt)
      ? nextTriggerAt : nextTriggerAt + 'Z';
    const diffMs = new Date(utc).getTime() - Date.now();
    if (diffMs <= 0) return 'Due';
    const diffSec = Math.floor(diffMs / 1000);
    const h = Math.floor(diffSec / 3600);
    const m = Math.floor((diffSec % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  }

  getRunForAgent(agentId: string): AgentRunResponse | null | undefined {
    return this.runProgress().get(agentId);
  }

  openCreateModal(): void {
    this.clearTimer(this.createCloseTimer);
    this.createCloseTimer = null;
    this.createClosing.set(false);
    this.saveError.set(null);
    this.agentForm.reset({ app_id: '', concept_ids: [], content_runs_per_concept: 1, trigger_interval: '', is_active: true });
    this.conceptsByApp.set([]);
    this.loadApps();
    this.showCreateModal.set(true);
  }

  closeCreateModal(): void {
    if (!this.showCreateModal() || this.createClosing()) return;
    this.createClosing.set(true);
    this.createCloseTimer = setTimeout(() => {
      this.showCreateModal.set(false);
      this.createClosing.set(false);
      this.createCloseTimer = null;
    }, 200);
  }

  openEditModal(agent: AgentResponse): void {
    this.clearTimer(this.editCloseTimer);
    this.editCloseTimer = null;
    this.editClosing.set(false);
    this.selectedAgent.set(agent);
    this.saveError.set(null);
    this.loadConceptsForApp(agent.app_id);
    this.agentForm.reset({
      app_id: agent.app_id,
      concept_ids: [...agent.concept_ids],
      content_runs_per_concept: agent.content_runs_per_concept,
      trigger_interval: agent.trigger_interval,
      is_active: agent.is_active,
    });
    this.showEditModal.set(true);
  }

  closeEditModal(): void {
    if (!this.showEditModal() || this.editClosing()) return;
    this.editClosing.set(true);
    this.editCloseTimer = setTimeout(() => {
      this.showEditModal.set(false);
      this.editClosing.set(false);
      this.selectedAgent.set(null);
      this.editCloseTimer = null;
    }, 200);
  }

  openDeleteModal(agent: AgentResponse): void {
    this.clearTimer(this.deleteCloseTimer);
    this.deleteCloseTimer = null;
    this.deleteClosing.set(false);
    this.selectedAgent.set(agent);
    this.saveError.set(null);
    this.showDeleteModal.set(true);
  }

  closeDeleteModal(): void {
    if (!this.showDeleteModal() || this.deleteClosing()) return;
    this.deleteClosing.set(true);
    this.deleteCloseTimer = setTimeout(() => {
      this.showDeleteModal.set(false);
      this.deleteClosing.set(false);
      this.selectedAgent.set(null);
      this.deleteCloseTimer = null;
    }, 200);
  }

  private beginClose(
    open: { (): boolean },
    closing: { (): boolean; set(value: boolean): void },
    timer: ReturnType<typeof setTimeout> | null,
    finish: () => void,
  ): ReturnType<typeof setTimeout> | null {
    if (!open() || closing()) return timer;
    closing.set(true);
    this.clearTimer(timer);
    return setTimeout(finish, 200);
  }

  private clearTimer(timer: ReturnType<typeof setTimeout> | null): void {
    if (timer) clearTimeout(timer);
  }

  submitCreate(): void {
    if (this.agentForm.invalid) { this.agentForm.markAllAsTouched(); return; }
    const v = this.agentForm.getRawValue();
    const payload: CreateAgentRequest = {
      app_id: v.app_id!,
      concept_ids: v.concept_ids ?? [],
      content_runs_per_concept: v.content_runs_per_concept ?? 1,
      trigger_interval: v.trigger_interval!,
      is_active: v.is_active ?? true,
    };
    this.isSaving.set(true);
    this.saveError.set(null);
    this.agentService.create(payload).subscribe({
      next: () => { this.isSaving.set(false); this.closeCreateModal(); this.loadAgents(); },
      error: err => { this.isSaving.set(false); this.saveError.set(err?.error?.detail ?? 'Failed to create agent.'); },
    });
  }

  submitEdit(): void {
    const agent = this.selectedAgent();
    if (!agent || this.agentForm.invalid) { this.agentForm.markAllAsTouched(); return; }
    const v = this.agentForm.getRawValue();
    const payload: UpdateAgentRequest = {
      app_id: v.app_id ?? undefined,
      concept_ids: v.concept_ids ?? undefined,
      content_runs_per_concept: v.content_runs_per_concept ?? undefined,
      trigger_interval: v.trigger_interval ?? undefined,
      is_active: v.is_active ?? undefined,
    };
    this.isSaving.set(true);
    this.saveError.set(null);
    this.agentService.update(agent.agent_id, payload).subscribe({
      next: () => { this.isSaving.set(false); this.closeEditModal(); this.loadAgents(); },
      error: err => { this.isSaving.set(false); this.saveError.set(err?.error?.detail ?? 'Failed to update agent.'); },
    });
  }

  confirmDelete(): void {
    const agent = this.selectedAgent();
    if (!agent) return;
    this.isSaving.set(true);
    this.saveError.set(null);
    this.agentService.delete(agent.agent_id).subscribe({
      next: () => { this.agents.update(list => list.filter(a => a.agent_id !== agent.agent_id)); this.isSaving.set(false); this.closeDeleteModal(); this.loadAgents(); },
      error: err => { this.isSaving.set(false); this.saveError.set(err?.error?.detail ?? 'Failed to delete agent.'); },
    });
  }

  private resumeActiveRuns(): void {
    this.agentService.listRuns({ skip: 0, limit: 20 }).subscribe({
      next: runs => {
        runs
          .filter(r => r.status === 'pending' || r.status === 'running')
          .forEach(r => this.pollRun(r.agent_id, r.run_id));
      },
      error: () => {},
    });
  }

  private clearRun(agentId: string): void {
    this.activePolls.delete(agentId);
    this.runProgress.update(m => {
      const next = new Map(m);
      next.delete(agentId);
      return next;
    });
  }

  private pollRun(agentId: string, runId: string): void {
    if (this.activePolls.has(agentId)) return;
    this.activePolls.add(agentId);
    if (!this.runProgress().has(agentId)) {
      this.runProgress.update(m => new Map(m).set(agentId, null));
    }

    timer(0, 2500).pipe(
      switchMap(() => this.agentService.getRun(runId)),
      tap(run => this.runProgress.update(m => new Map(m).set(agentId, run))),
      takeWhile(run => run.status === 'pending' || run.status === 'running', true),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: run => {
        if (run.status === 'completed' || run.status === 'failed') {
          this.activePolls.delete(agentId);
          setTimeout(() => {
            this.clearRun(agentId);
            this.loadAgents();
          }, 3000);
        }
      },
      error: () => this.clearRun(agentId),
    });
  }

  triggerManually(agent: AgentResponse): void {
    const agentId = agent.agent_id;
    if (this.runProgress().has(agentId)) return;
    this.runProgress.update(m => new Map(m).set(agentId, null));

    this.agentService.trigger(agentId).pipe(
      // Poll /runs until the new run appears (max 15 attempts ~37s)
      switchMap(() =>
        timer(1000, 2500).pipe(
          take(15),
          switchMap(() => this.agentService.listRuns({ agent_id: agentId, skip: 0, limit: 1 })),
          filter(runs => runs.length > 0),
          take(1),
        )
      ),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: runs => this.pollRun(agentId, runs[0].run_id),
      error: err => {
        this.clearRun(agentId);
        this.errorMsg.set(err?.error?.detail ?? 'Trigger failed.');
      },
    });
  }
}
